import "../styles/DataGrid.css";

import { Dispatch, Key, ReactElement, SetStateAction, UIEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Column as RdgColumn, DataGridHandle, SortColumn as RdgSortColumn } from "react-data-grid";

import { FullRightArrowDark } from "../../../../assets";
import { FilterCriteria, FilterOperator, SortCriteria } from "../../../../models";
import { ResultData } from "../../../../service/Shared";
import { roundToDecimalPlaces, safeLocalStorage } from "../../../../utils";
import { onFilterChangedEvent } from "../components/HeaderRenderer";
import { EditColumns, IconPosition } from "../models";
import { getFilterCriteriaWithCastedValues, mapColumnDefinitionToRdgColumn } from "../utils";
import { CursorDataGridProps } from "./CursorDataGrid";

export interface UseDataGridReturnData {
  filterCriteria: FilterCriteria[];
  setFilterCriteria: Dispatch<SetStateAction<FilterCriteria[]>>;
  resultData: readonly ResultData[];
  filteredRdgColumns: RdgColumn<ResultData>[];
  sortColumns: readonly RdgSortColumn[];
  onSort: (newSortColumns: RdgSortColumn[]) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  totalCount: number;
  filtersEnabled: boolean;
  setFiltersEnabled: Dispatch<SetStateAction<boolean>>;
  showFilterToggleSwitch: boolean;
  onExport: () => void;
  onVerticalScroll: (e: UIEvent<HTMLElement>) => void;
  onRefreshColumns: () => void; // for edit columns
  dataGridRef: (node: DataGridHandle) => void;
  iconArrow: ReactElement;
  handleScrollClick: () => void;
  iconPos: IconPosition;
  onHorizontalScroll: (e: UIEvent<HTMLElement>) => void;
  showScrollButton: boolean;
  totalRow?: ResultData;
  totalColumnNames?: string[]; // the names of the columns to calculate totals for
  selectableUuids: Set<Key>;
}

export const useCursorDataGrid = ({
  data,
  columns,
  filterable = false,
  sortable = true,
  clientSide = false,
  onChange,
  pagination = { pageSize: 10 },
  defaultSortingCriteria = [],
  refresh,
  showFilterToggle,
  triggerPagePrevious,
  onExternalExport,
  exportFileName = "",
  filterExportRows,
  filterExportColumns,
  infiniteScroll = false,
  editColumnsLocalStorageItemName,
  totalColumnNames,
  setSelectedRows,
}: CursorDataGridProps): UseDataGridReturnData => {
  const [resultData, setResultData] = useState<readonly ResultData[]>(data || []);
  const [sortColumns, setSortColumns] = useState<readonly RdgSortColumn[]>(
    sortable && defaultSortingCriteria ? defaultSortingCriteria : []
  );
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria[]>([]);

  const [dgStartCursor, setDgStartCursor] = useState<string | undefined>(pagination.startCursor);
  const [dgEndCursor, setDgEndCursor] = useState<string | undefined>(pagination.endCursor);
  const [dgBeforeCursor, setDgBeforeCursor] = useState<string>();
  const [dgAfterCursor, setDgAfterCursor] = useState<string>();

  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [filtersEnabled, setFiltersEnabled] = useState(false);

  // For infinite scroll
  const [onNextTriggered, setOnNextTriggered] = useState(true);
  const [scrollTop, setScrollTop] = useState(0); // Need this to ensure we don't call onNext multiple times within the scrollTolerence (see onScroll)

  const [editColumns, setEditColumns] = useState<EditColumns>(); // For editable columns
  const [iconPos, setIconPos] = useState<"left" | "right">("right");
  const [showScrollButton, setShowScrollButton] = useState(false);

  const [dataGrid, setDataGrid] = useState<DataGridHandle | null>();
  const dataGridRef = useCallback(
    (node: DataGridHandle) => {
      if (node !== null && dataGrid === undefined) {
        setDataGrid(node);
      }
    },
    [dataGrid]
  );

  // Filter out total row from selectable rows
  const selectableUuids = useMemo(
    () => new Set(resultData.filter((row) => row.id !== "total").map((r) => r.uuid as Key)),
    [resultData]
  );

  const onPrevious = (): void => {
    setDgBeforeCursor(dgStartCursor);
    setDgAfterCursor(undefined);
  };

  useEffect(() => {
    if (triggerPagePrevious !== undefined && hasPrevious) {
      onPrevious();
    }
  }, [triggerPagePrevious]);

  const onNext = (): void => {
    setOnNextTriggered((prevOnNextTriggered) => !prevOnNextTriggered);

    setDgBeforeCursor(undefined);
    setDgAfterCursor(dgEndCursor);
  };

  const onSort = (newSortColumns: RdgSortColumn[]): void => {
    setSortColumns(newSortColumns);
    // reset paging when changing sort order
    setDgBeforeCursor(undefined);
    setDgAfterCursor(undefined);
  };

  const pageSize = pagination.pageSize || 10;

  const onFilterChanged: onFilterChangedEvent = (
    columnKey: string,
    operator: FilterOperator,
    value: string,
    filterId: number | undefined
  ): void => {
    let starterFilters = filterCriteria.filter((f) => f.key !== columnKey);

    starterFilters = starterFilters.concat({
      key: columnKey,
      operator,
      value,
      filterId,
    });

    setFilterCriteria(starterFilters);
    // reset paging when changing filtering
    setDgBeforeCursor(undefined);
    setDgAfterCursor(undefined);
  };

  const masterRdgColumns: RdgColumn<ResultData>[] = mapColumnDefinitionToRdgColumn(
    columns,
    sortable,
    filterable,
    sortColumns,
    setSortColumns,
    filtersEnabled,
    filterCriteria,
    setFilterCriteria,
    onFilterChanged,
    resultData
  );

  const filteredRdgColumns = editColumns
    ? masterRdgColumns
        ?.filter((column) => editColumns[column.key].show)
        .sort((a, b) => editColumns[a.key].index - editColumns[b.key].index)
    : masterRdgColumns;

  const createTotalRow = (): ResultData => {
    const totalRow: ResultData = {
      id: "total",
      uuid: "total",
      [filteredRdgColumns.filter((col) => !totalColumnNames?.some((colName) => colName === col.key))[0].key ?? ""]:
        "Total",
    };

    totalColumnNames?.forEach((col) => {
      totalRow[col] = resultData.reduce((sum, row) => {
        const value = row[col];
        return roundToDecimalPlaces(sum + (typeof value === "number" ? value : 0), 5);
      }, 0);
    });

    return totalRow;
  };

  const getCurrentFilterCriteria = (criteria: FilterCriteria[]): FilterCriteria[] => {
    let tempFilterCriteria = [...criteria];

    if (criteria.some((fc) => fc.value === "" && fc.operator !== "contains")) {
      // If value is empty, remove that filter from a temp Array so that all data is being refetched
      tempFilterCriteria = tempFilterCriteria.filter((tfc) => !(tfc.value === "" && tfc.operator !== "contains"));
    }
    return tempFilterCriteria;
  };

  const getCurrentSortCriteria = (sortCols: readonly RdgSortColumn[]): SortCriteria[] => {
    return sortCols.map(
      (sc): SortCriteria => ({
        key: sc.columnKey,
        direction: sc.direction === "DESC" ? "desc" : "asc",
      })
    );
  };

  const onExport = (): void => {
    if (onChange) {
      let csvURL: string;

      const tempFilterCriteria = getCurrentFilterCriteria(filterCriteria);
      const sortCriteria = getCurrentSortCriteria(sortColumns);

      onChange({
        filtering: getFilterCriteriaWithCastedValues(tempFilterCriteria, columns),
        sorting: sortCriteria,
        paging: {
          pageSize: totalCount > 1000 ? 1000 : totalCount,
        },
      })
        .then((update) => {
          const exportResultData =
            filterExportRows !== undefined ? filterExportRows(update.resultData) : update.resultData;
          const exportColumns =
            filterExportColumns !== undefined ? filterExportColumns(filteredRdgColumns) : filteredRdgColumns;

          const separator = ",";
          const header = `${exportColumns.map((x) => x.name).join(separator)}\n`;
          const csv =
            header +
            exportResultData
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((row) => exportColumns.map((col) => (row[col.key] as any)?.text ?? row[col.key]).join(separator))
              .join("\n");

          const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          csvURL = window.URL.createObjectURL(csvData);
          const tempLink = document.createElement("a");
          tempLink.href = csvURL;
          tempLink.setAttribute("download", `${exportFileName}${new Date().toISOString()}_grid_data.csv`);
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
        })
        .finally(() => {
          URL.revokeObjectURL(csvURL);
        });
    }
  };

  /**
   * Updates the columns in the DataGrid based on the user's selection
   * stored in local storage.
   */
  const onRefreshColumns = (): void => {
    if (editColumnsLocalStorageItemName) {
      const storedObject = safeLocalStorage.getItem(editColumnsLocalStorageItemName);
      if (storedObject) {
        setEditColumns(JSON.parse(storedObject) as EditColumns);
        setFilterCriteria([]);
        setSortColumns([]);
      }
    }
  };

  const onPageChange = (onInfiniteScroll?: boolean, onReset?: boolean): void => {
    if (onChange) {
      const tempFilterCriteria = getCurrentFilterCriteria(filterCriteria);

      const sortCriteria = getCurrentSortCriteria(sortColumns);

      onChange({
        filtering: getFilterCriteriaWithCastedValues(tempFilterCriteria, columns),
        sorting: sortCriteria,
        paging: {
          pageSize,
          afterCursor: onReset ? undefined : dgAfterCursor,
          beforeCursor: onReset ? undefined : dgBeforeCursor,
        },
      }).then((update) => {
        if (onInfiniteScroll && !onReset) {
          setResultData((prevResultData) => prevResultData.concat(update.resultData));
        } else {
          setResultData(update.resultData);
        }

        if (update.paging?.startCursor) setDgStartCursor(update.paging.startCursor);
        if (update.paging?.endCursor) setDgEndCursor(update.paging.endCursor);
        setHasPrevious(update.paging.hasPreviousPage);
        setHasNext(update.paging.hasNextPage);
        setTotalCount(update.paging.totalCount);
      });
    }
  };

  // --- horizontal scroll indicator ---
  const onHorizontalScroll = (e: UIEvent<HTMLElement>): void => {
    if (!e.target) return;
    const { scrollLeft, scrollWidth, clientWidth } = e.target as HTMLElement;
    // Calculate midpoint of the scrollable content
    const midpoint = (scrollWidth - clientWidth) / 2;

    setIconPos(scrollLeft < midpoint ? "right" : "left");
  };

  const handleScrollClick = (): void => {
    const el = dataGrid?.element;
    if (!el) return;
    if (iconPos === "right") {
      el.scrollTo({
        left: el.scrollWidth - el.clientWidth,
        behavior: "smooth",
      });
    } else {
      el.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const el = dataGrid?.element;
    if (!el) return;

    const updateScrollButton = (): void => {
      setShowScrollButton(el.scrollWidth > el.clientWidth);
    };

    updateScrollButton(); // Initial check

    const observer = new ResizeObserver(() => {
      updateScrollButton();
    });

    observer.observe(el);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.unobserve(el);
      observer.disconnect();
    };
  }, [dataGrid]);

  // --- For infinite scrolling ---
  const onVerticalScroll = (e: UIEvent<HTMLElement>): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventTarget = e.target as any;
    // In certain situations (eg. different zoom levels) browsers can miscalculate the scrollTop because of fractional pixels, so we add a small px tolerance
    const scrollTopTolerance = 5;

    if (
      eventTarget.scrollTop > scrollTopTolerance + scrollTop &&
      eventTarget.scrollTop + scrollTopTolerance + eventTarget.clientHeight >= eventTarget.scrollHeight
    ) {
      setScrollTop(eventTarget.scrollTop);
      if (hasNext) onNext();
    }
  };

  // --- Infinite scroll ---
  useEffect(() => {
    // On scroll
    if (infiniteScroll) onPageChange(true);
  }, [onNextTriggered]);
  useEffect(() => {
    // On filter / sort
    if (infiniteScroll) {
      setScrollTop(0);
      onPageChange(undefined, true);
    }
  }, [sortColumns, filterCriteria, refresh]);

  // --- Regular pagination ---
  useEffect(() => {
    if (!infiniteScroll) onPageChange();
  }, [sortColumns, filterCriteria, dgBeforeCursor, dgAfterCursor, refresh]);

  useEffect(() => {
    if (clientSide) {
      const nextData = data || [];
      setResultData(nextData);
      setTotalCount(nextData.length);
      setHasNext(false);
      setHasPrevious(false);
    }
  }, [clientSide, data]);

  // For moving controlls outside the data grid component
  useEffect(() => {
    if (onExternalExport !== undefined) {
      onExport();
    }
  }, [onExternalExport]);

  // Clear selected rows when data changes
  useEffect(() => {
    if (setSelectedRows) setSelectedRows(new Set());
  }, [resultData]);

  return {
    resultData,
    filterCriteria,
    setFilterCriteria,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
    filteredRdgColumns,
    onSort,
    sortColumns,
    totalRow: createTotalRow(),
    totalCount,
    filtersEnabled,
    setFiltersEnabled,
    showFilterToggleSwitch: showFilterToggle ?? filterable,
    onExport,
    onVerticalScroll,
    onRefreshColumns,
    dataGridRef,
    iconArrow:
      iconPos === "right" ? (
        <FullRightArrowDark className="rightArrow" />
      ) : (
        <FullRightArrowDark className="leftArrow" />
      ),
    handleScrollClick,
    iconPos,
    onHorizontalScroll,
    showScrollButton,
    selectableUuids,
  };
};
